import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; 
import { UserContext } from '../UserContext';
import './JoinGroup.css';

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

const JoinGroup = () => {
  const { user, setUser } = useContext(UserContext); // ✅ Ensure `setUser` exists
  const navigate = useNavigate();
  const [joinCode, setJoinCode] = useState('');
  const [message, setMessage] = useState('');
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const handleJoinGroup = async (e) => {
    e.preventDefault();
  
    if (!joinCode.trim()) {
      setMessage("Please enter a valid join code.");
      return;
    }
  
    if (!user || !user.id) {
      setMessage("User ID is missing. Please log in again.");
      console.error("🚨 Missing user ID:", user);
      return;
    }
    
    // ✅ Ensure user has a weight entry before joining
    const hasWeight = user.startingWeightG !== null || (user.weightUpdates && user.weightUpdates.length > 0);
    if (!hasWeight) {
      setMessage("⚠️ You must set your starting weight in your profile before joining a group.");
      return;
    }
    
    setLoading(true);
  
    try {
      console.log("📤 Sending join request:", { userId: user.id, joinCode });
  
      const response = await axios.post(
        `${API_BASE_URL}/group/join`, 
        { joinCode }, 
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
  
      console.log("✅ Join Response:", response.data);
  
      if (!response.data.groupId) {
        throw new Error("❌ No `groupId` returned from server.");
      }
  
      const { groupId, name, adminName, members, totalWeightLost } = response.data;
  
      // ✅ Ensure `setUser` is available before updating context
      if (setUser) {
        setUser(prevUser => ({
          ...prevUser,
          userGroups: [...(prevUser.userGroups || []), groupId],
        }));
      }
  
      setMessage(`Successfully joined: ${name}`);
      setGroupData({ groupId, name, adminName, members, totalWeightLost });
  
      // ✅ Use a short delay before redirecting to allow state update
      setTimeout(() => {
        console.log(`🔄 Navigating to /group/${groupId}`);
        navigate(`/group/${groupId}`);
      }, 1500); // Reduced to 1.5s for a better experience
  
    } catch (error) {
      console.error("❌ Join Group Error:", error.response?.data || error);
      setMessage(error.response?.data?.error || "Error joining group.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="join-group-wrapper">
      <div className="join-group-container">
        <h2 className="join-group-title">Join a Group</h2>

        {message && <p className="join-group-message">{message}</p>}
        {loading && <p className="join-group-loading">Joining group...</p>}

        <form onSubmit={handleJoinGroup} className="join-group-form">
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder="Enter Join Code"
            className="join-group-input"
          />
          <button type="submit" className="button" disabled={loading}>
            {loading ? "Joining..." : "Join Group"}
          </button>
        </form>

        {groupData && groupData.name && (
          <div className="join-group-info">
            <h3 className="join-group-info-title">Group Details</h3>
            <p><strong>Name:</strong> {groupData.name}</p>
            <p><strong>Admin:</strong> {groupData.adminName}</p>
            <p><strong>Current Members:</strong> {groupData.members?.length || 0}</p>
            <p><strong>Total Weight Lost:</strong> {groupData.totalWeightLost || 0} lbs</p>
            <p className="join-group-redirect">Redirecting to group page...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinGroup;
